import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, HostListener } from '@angular/core';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { ImageViewerComponent } from 'src/app/shared/components/image-viewer/image-viewer.component';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { environment } from 'src/environments/environment';
import { ImageBuilderDraft, ImageGeneratorService } from '../image-generator.service';
import { AuthService } from 'src/app/auth.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-image-refiner',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslateModule,
    MatDividerModule,
    MatSelectModule,
    MatTabsModule,
    ImageViewerComponent,
  ],
  templateUrl: './image-refiner.component.html',
  styleUrls: ['./image-refiner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageRefinerComponent {
  draft = input.required<ImageBuilderDraft>();
  close = output<void>();
  imageSelected = output<{ url: string; alt: string; storagePath: string }>();
  updated = output<ImageBuilderDraft>();

  private fb = inject(FormBuilder);
  private imageService = inject(ImageGeneratorService);
  private sanitizer = inject(DomSanitizer);
  private storage = inject(Storage);
  private confirmMenuService = inject(ConfirmMenuService);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private errorHandler = inject(ErrorHandlerService);

  regenerateForm: FormGroup;
  selectedVersionId = signal<string | null>(null);
  isRegenerating = signal(false);
  currentSecureImageUrl = signal<SafeUrl | null>(null);

  isMobile = signal(window.innerWidth < 599);

  @HostListener('window:resize')
  onResize() {
    this.isMobile.set(window.innerWidth < 599);
  }
  attachedFiles = signal<{ id: string; file?: File; name: string; type: string; url?: string; uploading: boolean; filename?: string }[]>([]);

  aspectRatios = [
    { value: '1:1', label: 'MySpace.AIImageResolutions.Square' },
    { value: '16:9', label: 'MySpace.AIImageResolutions.Landscape' },
    { value: '9:16', label: 'MySpace.AIImageResolutions.Portrait' },
    { value: '4:3', label: 'Standard (4:3)' },
    { value: '3:4', label: 'Portrait (3:4)' },
  ];

  models = [
    { value: 'fast', label: 'ImageGenerator.Model.Fast' },
    { value: 'pro', label: 'ImageGenerator.Model.Pro' },
  ];

  currentVersion = computed(() => {
    const draft = this.draft();
    const vId = this.selectedVersionId();
    if (!draft || !draft.versions.length) return null;
    return draft.versions.find((v) => v._id === vId) || draft.versions.find((v) => v._id === draft.currentVersionId) || draft.versions[draft.versions.length - 1];
  });

  reversedVersions = computed(() => {
    const draft = this.draft();
    if (!draft || !draft.versions) return [];
    return [...draft.versions].reverse();
  });

  constructor() {
    this.regenerateForm = this.fb.group({
      instruction: ['', [Validators.required, Validators.minLength(3)]],
      aspectRatio: ['16:9', Validators.required],
      model: ['pro'],
    });

    effect(() => {
      const ver = this.currentVersion();
      if (ver) {
        // Update form with current version's settings
        this.regenerateForm.patchValue(
          {
            aspectRatio: ver.aspectRatio,
            model: ver.model || 'pro',
          },
          { emitEvent: false }
        );

        if (ver.storagePath) {
          this.currentSecureImageUrl.set(null); // Loading
          this.getDownloadURL(ver.storagePath)
            .then((url) => {
              const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
              this.currentSecureImageUrl.set(safeUrl);
            })
            .catch((err) => {
              console.error('Failed to load secure image', err);
            });
        }
      }
    });

    // Initialize selectedVersionId when draft changes
    effect(() => {
      const draft = this.draft();
      if (draft) {
        const latest = draft.versions[draft.versions.length - 1];
        this.selectedVersionId.set(draft.currentVersionId || (latest ? latest._id : null));
      }
    });
  }

  getDownloadURL(path: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const reference = ref(this.storage, path);
      getDownloadURL(reference)
        .then((url) => resolve(url))
        .catch((err) => {
          if (path.includes(environment.publicBucketStorage)) {
            const parts = path.split(environment.publicBucketStorage);
            if (parts.length > 1) {
              const urlGs = `gs://${environment.publicBucketStorage}${parts[1]}`;
              const storageRef = ref(this.storage, urlGs);
              getDownloadURL(storageRef).then(resolve).catch(reject);
            } else {
              reject(err);
            }
          } else {
            reject(err);
          }
        });
    });
  }

  selectVersion(versionId: string) {
    this.selectedVersionId.set(versionId);
  }

  insertImage(event?: Event) {
    if (event) event.stopPropagation();
    const version = this.currentVersion();
    if (!version) return;
    this.imageSelected.emit({ url: version.imageUrl, alt: version.prompt, storagePath: version.storagePath });
  }

  regenerate() {
    if (this.regenerateForm.invalid) return;
    const { instruction, aspectRatio, model } = this.regenerateForm.value;
    const draft = this.draft();
    if (!draft) return;

    this.isRegenerating.set(true);

    const readyAttachments = this.attachedFiles().filter(f => !f.uploading && f.url);
    const mediaContexts = readyAttachments.map(f => ({ url: f.url!, type: f.type, mimeType: 'image/jpeg', filename: f.name }));

    this.imageService
      .addVersion({
        draftId: draft._id,
        instruction,
        aspectRatio,
        model,
        referenceVersionId: this.selectedVersionId() || undefined,
        mediaContexts: mediaContexts.length > 0 ? mediaContexts : undefined
      })
      .subscribe({
        next: (updatedDraft) => {
          this.updated.emit(updatedDraft);
          this.isRegenerating.set(false);
          this.regenerateForm.get('instruction')?.reset();
          this.attachedFiles.set([]);
        },
        error: (err) => {
          console.error('Failed to regenerate', err);
          this.errorHandler.handleAiRestrictedError(err);
          this.isRegenerating.set(false);
        },
      });
  }

  deleteVersion(event: Event, versionId: string) {
    event.stopPropagation();
    const draft = this.draft();
    if (!draft) return;

    this.confirmMenuService.confirm(event.target as HTMLElement, 'Global.AreYouSure', 'delete_outline').subscribe(() => {
      this.imageService.deleteVersion(draft._id, versionId).subscribe({
        next: (updatedDraft) => {
          if (updatedDraft) {
            this.updated.emit(updatedDraft);
            if (this.selectedVersionId() === versionId) {
              this.selectedVersionId.set(updatedDraft.currentVersionId || (updatedDraft.versions.length ? updatedDraft.versions[updatedDraft.versions.length - 1]._id : null));
            }
          } else {
            // Draft deleted
            this.close.emit();
          }
        },
        error: (err) => console.error('Failed to delete version', err),
      });
    });
  }

  // --- Attachments & Voice ---
  
  allFilesDoneUploading() {
    return this.attachedFiles().every((f) => !f.uploading);
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0]; // Only allow 1 image for now
    if (!file.type.startsWith('image/')) {
      this.toast.snackbar.open(this.translate.instant('FileUploader.UnsupportedFileType') || 'File type not supported', 'Close', { duration: 3000 });
      return;
    }

    // Remove any existing image before adding new one
    const existingImage = this.attachedFiles().find(f => f.type === 'image');
    if (existingImage) {
      this.removeAttachment(existingImage.id);
    }

    const fileId = Date.now().toString();
    this.attachedFiles.update((filesArray) => [...filesArray, { id: fileId, file, name: file.name, type: 'image', uploading: true }]);
    this.uploadFileInternal(file, fileId);
    event.target.value = '';
  }

  private async uploadFileInternal(file: File, fileId: string) {
    try {
      const draftId = this.draft()?._id;
      const filename = `image_generator_attachments/${draftId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const snapshot: any = await this.authService.uploadFile(file, filename, undefined, true);
      const gsUrl = `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`;

      this.attachedFiles.update((filesArray) => filesArray.map((f) => (f.id === fileId ? { ...f, url: gsUrl, filename, uploading: false } : f)));
    } catch (err) {
      console.error('File upload failed', err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Upload failed', 'Close', { duration: 3000 });
      this.removeAttachment(fileId);
    }
  }

  removeAttachment(fileId: string) {
    const fileToRemove = this.attachedFiles().find((f) => f.id === fileId);
    if (fileToRemove && fileToRemove.filename) {
      this.authService.deleteFile(fileToRemove.filename).catch((err) => console.error('Failed to delete file from storage', err));
    }
    this.attachedFiles.update((filesArray) => filesArray.filter((f) => f.id !== fileId));
  }
}
